import {
  Button,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@ibsys/kit';
import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Тексты окна подтверждения записи донастройки в слой отбора */
export type SaveLayerConfirmation = {
  /** Заголовок-вопрос с названием слоя */
  title: string;
  /** Предупреждение, кто ещё увидит изменения; не задано — окно только спрашивает подтверждение */
  description?: string;
};

type Props = SaveLayerConfirmation & {
  /** Кнопка сохранения, обёрнутая в `DialogTrigger`: окно открывается по ней */
  trigger: ReactNode;
  /** Записать донастройку в слой отбора */
  onConfirm: () => void;
};

/**
 * Подтверждение записи донастройки в слой отбора.
 *
 * Запись меняет сохранённый вид слоя, а у общего слоя — и вид у других пользователей, поэтому
 * кнопка сохранения сначала спрашивает. «Сохранить» записывает донастройку, «Отменить» и закрытие
 * окна ничего не меняют: донастройка остаётся при пользователе.
 */
export const SaveLayerConfirmDialog: FC<Props> = ({ trigger, title, description, onConfirm }) => {
  const { t } = useTranslation(['tablePresets', 'common']);
  const [open, setOpen] = useState(false);

  const handleConfirm = () => {
    setOpen(false);
    onConfirm();
  };

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      {trigger}
      <DialogContent className="sm:max-w-xl">
        <DialogHeader className="min-w-0">
          {/* Крестик окна стоит поверх содержимого: место под него оставляет сам заголовок */}
          <DialogTitle className="pr-6 wrap-anywhere">
            <span className="leading-snug">{title}</span>
          </DialogTitle>
        </DialogHeader>
        {description && <DialogDescription>{description}</DialogDescription>}
        <DialogFooter className="flex-wrap">
          <DialogClose asChild>
            <Button variant="secondary">{t('common:cancel')}</Button>
          </DialogClose>
          <Button onClick={handleConfirm}>{t('tablePresets:saveChangesConfirm')}</Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
